import * as React from "react";
import styled from 'styled-components';
import moment from 'moment';
import { defineMessages, useIntl } from 'react-intl';
import { StatusWrap } from "../styled";
import { connect } from "react-redux";
import { Routine } from "redux-saga-routines";
import {Action, ActionFunctionAny} from "redux-actions";
import { getDevice, syncDevice, connectDevice } from "@Safemate/Settings/Store/routines";
import { AppState } from "@Safemate/Store/types";
import { Device } from "@Safemate/Settings/types";
import { supportGsmStrength } from "@Safemate/Settings/tabDefinition";
import { Status } from "@Safemate/DeviceList/DeviceList/TableElements";
import {Capabilities} from "@Safemate/Settings/Store/types";
import { getAllStatuses } from "@Safemate/DeviceList/DeviceList/utils";
import { StatusText } from "@Safemate/DeviceList/DeviceList/deviceStatus";

const translatedText = defineMessages({
  invalid: {
    id: 'statusBarInvalidDate',
    defaultMessage: 'Har aldri oppnådd kontakt'
  },
  updateConfig: {
    id: 'updateConfigBtn',
    defaultMessage: 'Oppdater konfigurasjon'
  },
  nullRssi: {
    id: 'noSignal',
    defaultMessage: 'Ingen signal'
  }
});

const Item = styled.div`
  display: flex;
  flex-direction: row;
  margin: 0 5px;
  padding: 5px;
  align-items: center;
  border-right: ${props => props.theme.colors.border};
`;

const W = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
`;

const Text = styled.p`
  ::first-letter {
    text-transform: capitalize;
  }
  margin: 0
    ${({
      theme: {
        margins: { standard }
      }
    }) => standard}
    0 0;
  padding: 0;
  border: 0;
  color: ${({
    theme: {
      colors: { textPrimary }
    }
  }) => textPrimary};
  & :last-child {
    margin-right: 0;
  }
`;

const Event = styled(Text)`

  padding: 5px;
  border-radius: 5px;
  background: ${props => props.theme.colors.textPrimary};
  color: ${props => props.theme.colors.backgroundPrimary};

`;

const HighlightText = styled.span`
  color: ${({
    theme: {
      colors: { textHighlight }
    }
  }) => textHighlight};
`;

interface StatusBarProps{
  updatingDevice: boolean;
  deviId: number
  device: Device;
  syncDevice: Routine<ActionFunctionAny<Action<any>>>;
  getDevice: Routine<ActionFunctionAny<Action<any>>>;
  connectDevice: Routine<ActionFunctionAny<Action<any>>>;
  capabilities: Capabilities;
}

const mapStateToProps = ({ settings: {device: { updatingDevice, device }}}: AppState) => {
  return {
    updatingDevice,
    device
  }
}

const mapDispatchToProps = {
  syncDevice,
  getDevice,
  connectDevice
}

export const StatusBar = connect(mapStateToProps, mapDispatchToProps)(({ device: { battery, lastMsgIn}, device, deviId, getDevice}: StatusBarProps) => {

  const { formatMessage } = useIntl();

  React.useEffect(() => {
    if (!deviId) return;

    getDevice({deviceId: deviId});

    const intervalId = window.setInterval(() => {
      getDevice({deviceId: deviId});
    }, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [deviId]);

  const statusDate = moment
      .utc(lastMsgIn)
      .local()
      .format('DD.MM.YYYY HH:mm');

  const statusLastContact =
    statusDate === 'Invalid date'
      ? formatMessage(translatedText.invalid)
      : statusDate;

  const gsmSupport = supportGsmStrength.get(device.dehaId);

  let strength = 0

  if(gsmSupport) {

    const offset = gsmSupport.low === 0 ? 0 : -(gsmSupport.low);
    const scale =  100 / (gsmSupport.high + offset);
    strength = (device.gsmStrength + offset) * scale;

  }

  if(!device || !device.deviId) return null;

  const deviceEvents = getAllStatuses(device);

  return (
    <StatusWrap>
      <W>
      {!lastMsgIn ? formatMessage({id: "neverHadContact", defaultMessage: "Denne enheten har aldri hatt kontakt med Safemate"}) :
      <Item>
        <Text>
          {formatMessage({id: "statusBarLastConfig", defaultMessage: "Siste kontakt: "})}
        </Text>
        <Text>
          <HighlightText>{statusLastContact}</HighlightText>
        </Text>
      </Item>}
      <Item>
        <Text>
          {formatMessage({id: "battery", defaultMessage: "Batteri: "})}:
        </Text>
        <Text>
          <HighlightText>{battery ? `${battery}%` : formatMessage({id: "unknown", defaultMessage: "Ukjent"})}</HighlightText>
        </Text>
      </Item>
      <Item>
        <Text>
          <Status
            device={device}
            beta={false}
            isAhp={false}
          />
        </Text>
        <Text>
          {formatMessage({id: "events", defaultMessage: "Hendelser: "})}:
        </Text>
        {deviceEvents.length > 0 ? deviceEvents.map(status => {
          return(
          <Text>
            <Event>{formatMessage({id: status.status.text, defaultMessage: status.status.text})}</Event>
          </Text>)
        }) : <Text><Event>{formatMessage({id: StatusText.DEFAULT, defaultMessage: StatusText.DEFAULT})}</Event></Text>}
      </Item>
      </W>
    </StatusWrap>
  )
})